<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useNavCollapse } from "@/design/composables/use-nav-collapse";
import { useCan } from "@/features/auth/api/can";
import { usePipelines } from "@/features/board/api/use-board";
import { useComponentScreens } from "@/features/component-views/api/use-component-view";
import { useBilledByHq } from "@/features/organization/api/use-organization";
import { usePlatformAccess } from "@/features/platform/api/use-platform";
import { useStructure } from "@/features/structure/api/use-structure";
import { useHomeSteps } from "@/pages/use-home-steps";
import { useMyAccess } from "@/shared/access/use-access";
import { entityMenu } from "./entity-menu";
import { navMessages } from "./i18n/nav";
import NavFooter from "./NavFooter.vue";
import NavGroup from "./NavGroup.vue";
import NavLink from "./NavLink.vue";
import { COUNT_TONE, NAV_GROUPS, NAV_ITEMS, type NavRow, visibleItems } from "./nav-items";
import { useNavCounts } from "./use-nav-counts";

/**
 * Navigation: a fixed section in code, a dynamic section from metadata.
 *
 * The dynamic half is the point. If the menu were code, every entity the builder
 * creates would need a front-end deploy — and the zero-deploy promise would die in
 * the navigation while the backend held up its end (F-ADR-009).
 *
 * On a phone this renders inside a drawer rather than as a permanent rail: 240px of
 * sidebar on a 360px screen leaves no room for the data it points at.
 *
 * ── O QUE ESTE ARQUIVO DEIXOU DE DECIDIR ──────────────────────────────────
 *
 * Os destinos. Eles vêm de `nav-items.ts`, que a paleta lê também — eram duas
 * listas à mão e já haviam divergido em cinco telas. Aqui ficou só a montagem:
 * quais grupos, em que ordem, com que número ao lado.
 */
const emit = defineEmits<{ navigate: [] }>();

/**
 * Collapsed shows icons only. Never on the mobile DRAWER — that is already an
 * overlay you opened on purpose, and shrinking it to glyphs would make it
 * useless for the one job it has. `revealed` é o rail apontado pelo mouse: o
 * painel já está com 240px por cima do conteúdo, então esconder o rótulo ali
 * seria mostrar uma coluna larga e vazia.
 */
const props = defineProps<{ drawer?: boolean; revealed?: boolean }>();
const { collapsed } = useNavCollapse();
const narrow = computed(() => collapsed.value && !props.drawer && !props.revealed);

const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: navMessages });

const route = useRoute();
const { data: entities } = useStructure();
const { data: pipelines } = usePipelines();
const counts = useNavCounts();

/**
 * Os dois sinais que decidem o que existe para esta sessão, e são perguntas
 * diferentes: uma é "pode administrar este workspace", a outra "pode administrar
 * a instalação". Quem filtra é `visibleItems`, a mesma função que a paleta usa —
 * um item que 403 é pior do que item nenhum.
 */
const { data: platformAdmin } = usePlatformAccess();
const isAdmin = useCan("admin");
const { data: moduleAccess } = useMyAccess();
/** Num workspace da rede a Cobrança é da matriz, e não se oferece aqui. */
const { billedByHq } = useBilledByHq(isAdmin);

/**
 * "Começar" só enquanto o primeiro dia do dono não acabou — a mesma conta do título
 * de `HomePage`. Para quem foi convidado, e depois disso, a casa é "Hoje".
 */
const isOwner = useCan("owner");
const { steps } = useHomeSteps();
const homeKey = computed(() =>
	isOwner.value && !steps.value.every((s) => s.done) ? "items.home.label" : "items.home.labelDone",
);

const groups = computed(() => {
	const visible = visibleItems(NAV_ITEMS, {
		isAdmin: isAdmin.value,
		disabledModules: moduleAccess.value?.disabledModules,
		platformAdmin: Boolean(platformAdmin.value),
		billedByHq: billedByHq.value,
	});
	return NAV_GROUPS.map((group) => ({
		...group,
		label: t(group.labelKey),
		rows: visible
			// `footer` sai da lista que rola e reaparece no pé — ver `NavFooter`.
			.filter((item) => item.group === group.key && !item.footer)
			.map<NavRow>((item) => ({
				to: item.to,
				label: t(item.to === "/" ? homeKey.value : item.labelKey),
				icon: item.icon,
				badge: item.count ? counts.value[item.count] : undefined,
				tone: item.count ? COUNT_TONE[item.count] : undefined,
				...(item.nested && route.path.startsWith(`${item.to}/`) ? { active: true } : {}),
			})),
	}));
});

/**
 * OS ATALHOS — os sete de cima, soltos e sem cabeçalho.
 *
 * Eles não são um grupo: são o topo. Começar, Conversas, Canais, Pedidos de
 * ajuda, Contatos e Funis são o que se abre todo dia — os seis em que há alguém
 * do outro lado esperando. Um cabeçalho "Trabalho" acima
 * deles cobra uma linha inteira para nomear o óbvio — além de dar um chevron que
 * ninguém quer clicar, porque fechar isto é fechar o painel. É o `sidebarTopItems`
 * da V2, e é por isso que lá eles são `RouterLink` direto.
 *
 * O grupo continua existindo em `nav-items.ts` (`group: "trabalho"`): a paleta lê
 * a mesma tabela e ali o agrupamento vale. O que muda é só o desenho.
 */
const shortcuts = computed(
	() => groups.value.find((group) => group.key === "trabalho")?.rows ?? [],
);

/**
 * O resto, MENOS `/settings` — que desceu ao rodapé fixo, como na V2. Ela sai da
 * lista que rola sem sair do produto: `NAV_ITEMS` continua a declarando, a paleta
 * continua a encontrando e `nav-items.spec` continua provando a cobertura de
 * rotas. O que se moveu foi o lugar em que ela é desenhada.
 */
const rest = computed(() => groups.value.filter((group) => group.key !== "trabalho"));

/**
 * O que o tenant escreveu: um dado por linha, com as telas e o funil dele como vistas
 * (`entity-menu.ts`). Funis só guarda o funil de um dado que não está no menu — o
 * clique-por-funil que a V2 tinha, sem a mesma coisa duas vezes.
 */
const { data: screens } = useComponentScreens();
const menu = computed(() =>
	entityMenu({
		entities: entities.value ?? [],
		pipelines: pipelines.value ?? [],
		screens: screens.value ?? [],
		path: route.path,
		funnel: route.query.funil,
		noGroup: t("menuGroup.none"),
	}),
);
</script>

<template>
	<!-- `mt-5` entre grupos e não um `gap` uniforme: os 20px são a medida da V2 e
	     só valem ENTRE cabeçalhos — os atalhos do topo entram colados, porque não
	     têm cabeçalho para separar de nada. Com `gap` no `<nav>` o primeiro bloco
	     ganhava um respiro que ninguém pediu e os grupos perdiam o deles. -->
	<div class="flex min-h-0 flex-1 flex-col">
		<nav
			class="nav-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-8 pt-2"
			:class="narrow ? 'nav-scroll--bare px-1.5' : 'px-2.5'"
			:aria-label="t('nav.main')"
		>
			<!-- OS ATALHOS, soltos e sem cabeçalho — o topo da coluna, como na V2.
			     Nunca recolhem: fechar isto seria fechar o painel. -->
			<ul class="space-y-0.5">
				<li v-for="row in shortcuts" :key="row.to">
					<NavLink :row="row" :narrow="narrow" @navigate="emit('navigate')" />
				</li>
			</ul>

			<!-- O assunto do tenant, e por isso NÃO recolhível: recolher o que este
			     workspace criou é esconder o motivo de ele existir. -->
			<NavGroup
				v-for="[group, rows] in menu.groups"
				:key="group"
				class="mt-6"
				:label="group"
				:rows="rows"
				:narrow="narrow"
				@navigate="emit('navigate')"
			/>

			<NavGroup
				class="mt-6"
				:label="t('groups.funis')"
				slug="funis"
				:default-open="true"
				:rows="menu.funnels"
				:narrow="narrow"
				@navigate="emit('navigate')"
			/>

			<NavGroup
				v-for="group in rest"
				:key="group.key"
				class="mt-6"
				:label="group.label"
				:slug="group.key"
				:default-open="group.defaultOpen"
				:rows="group.rows"
				:narrow="narrow"
				@navigate="emit('navigate')"
			/>

			<!-- O rodapé "⌘K para buscar" saiu daqui, e não do produto: a barra de cima
			     passou a desenhar a busca como um campo, com a lupa, a palavra e o mesmo
			     `⌘K` ao lado. Dizer o atalho duas vezes na mesma tela era o tipo de
			     lembrete que ocupa o pé de uma coluna todo dia para ser lido uma vez.

			     Configurações também não está aqui: ela é o `NavFooter`, colado embaixo
			     e fora deste scroll. -->
		</nav>

		<!-- Na GAVETA do telefone não há coluna com pé desenhado por fora, e o menu é
		     a tela inteira: o rodapé entra aqui, colado embaixo da lista. No desktop
		     quem o desenha é o `AppRail`, porque lá ele fica ABAIXO do rail e o rail
		     é quem sabe a altura. -->
		<NavFooter v-if="drawer" @navigate="emit('navigate')" />
	</div>
</template>

<style scoped>
/* A barra como a V2 a desenha (n-scrollbar): o trilho não reserva espaço a mais
   que um fio, e o polegar SÓ existe enquanto o mouse está sobre o menu. Dois
   dialetos para o mesmo fato: Firefox e Chromium novos leem scrollbar-color;
   WebKit antigo lê os pseudo-elementos. Transparente ≠ none: o gutter fino fica
   estável e nada salta quando o polegar aparece. */
.nav-scroll {
	scrollbar-width: thin;
	scrollbar-color: transparent transparent;
}
.nav-scroll:hover {
	scrollbar-color: var(--color-border) transparent;
}
.nav-scroll::-webkit-scrollbar {
	width: 6px;
}
.nav-scroll::-webkit-scrollbar-thumb {
	background: transparent;
	border-radius: 3px;
}
.nav-scroll:hover::-webkit-scrollbar-thumb {
	background: var(--color-border);
}

/* RECOLHIDO, A BARRA NEM RESERVA O FIO.
 *
 * Nos 50px do rail o gutter de ~11px saía todo do lado direito: os traços,
 * centrados no que sobrava, ficavam com 10px à esquerda e 21px à direita — a
 * folga torta que o dono viu, e que o pé do menu (fora deste scroll, centrado
 * nos 50px inteiros) denunciava linha a linha. Ninguém perde nada: o ponteiro
 * sobre esta coluna ABRE o painel de 216px, então a barra estreita nunca chega
 * a ser vista, e a roda continua rolando. */
.nav-scroll--bare {
	scrollbar-width: none;
}
.nav-scroll--bare::-webkit-scrollbar {
	width: 0;
}
</style>
